import { createContext, useContext } from "react";

import type {
  Mission,
  MissionsCampaign,
} from "@/campaigns/modules/missions/types";
import { currentMission } from "@/campaigns/modules/missions/types";

type Selection = {
  selectedId: number | null;
  select: (id: number) => void;
};

/** Provided by `MissionSelectionProvider` (missions-sections.tsx) around the landscape panes. */
export const MissionSelectionContext = createContext<Selection | null>(null);

/** The step to open by default: the one the player is on; once everything is done, the last. */
const defaultMission = (mc: MissionsCampaign): Mission | undefined =>
  currentMission(mc) ?? mc.missions[mc.missions.length - 1];

/**
 * The selected step of `mc`, resolved: the player's pick while it still exists on the stream
 * (the step list is rebuilt on every progress push), else the default. Outside a provider the
 * pick is always the default and `select` is a no-op.
 */
export const useMissionSelection = (mc: MissionsCampaign) => {
  const ctx = useContext(MissionSelectionContext);
  const picked = ctx?.selectedId ?? null;
  const mission =
    (picked != null ? mc.missions.find((m) => m.id === picked) : undefined) ??
    defaultMission(mc);
  return {
    mission,
    select: ctx?.select ?? (() => {}),
  };
};
